import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { FileUploader } from '@/common/components/FileUploader'
import MaskedTextField from '@/common/components/MaskedTextField'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import useCompany from '@/common/hooks/queries/useCompany'
import useDebounce from '@/common/hooks/useDebounce'
import { ErrorResponse } from '@/common/types/errorResponse'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency, formatDateRange } from '@/common/utils/formatters'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { handleServerError } from '@/common/utils/handleServerError'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import useAdvanceGovernmentAllocationEntry from '@/features/privateCompanies/hooks/useAdvanceGovernmentAllocationEntry'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/privateCompanies/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { useCompanyBranch } from '@/features/privateCompanies/hooks/useCompanyBranch'
import { useUpdateCompanyGovernmentAllocationEntry } from '@/features/privateCompanies/hooks/useUpdateCompanyGovernmentAllocationEntry'
import { useUpdateCompanyGovernmentAllocationEntryFile } from '@/features/privateCompanies/hooks/useUpdateCompanyGovernmentAllocationEntryFile'
import {
  GovernmentAllocationEntryUpdateValidationSchema,
  updateSchema,
} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { EntryGovernmentAllocationDetails } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryGovernmentAllocationDetails'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/privateCompanies/services/allowedCategoriesAndProductsTransformer'
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import { CompanyGovernmentAllocationEntryData } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { filterOptions } from '@/features/users/constants/filterOptions.constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { CalendarDaysIcon, SearchIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { FilterOptionOption } from 'react-select'
import CreateEntryProductCategoryModal from '../CreateEntryProductCategoryModal'

export const GovernmentAllocationEntryCreatedForm = ({
  companyGovernmentAllocation,
}: {
  companyGovernmentAllocation: CompanyGovernmentAllocationEntryData
}) => {
  const { id, companyId } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const passedEveryRequirement = companyGovernmentAllocation?.requirement_list_summary?.steps?.every(
    (step) => step.passed,
  )

  const [file, setFile] = useState<Blob>()
  const [fileName, setFileName] = useState('')
  const [baseError, setBaseError] = useState('')

  const [selectedAllowedCategorySummaryRecord, setSelectedAllowedCategorySummary] = useState<ClientCompanyAllowedCategoryAndProductData & { id: string }>()

  const useFormMethods = useForm<GovernmentAllocationEntryUpdateValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      entry_product_categories: [],
    },
  })

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useFormMethods

  const { data: company } = useCompany({ id: companyId || '' })

  const { data: branchData } = useCompanyBranch(companyGovernmentAllocation?.government_allocation?.branch_id || '')

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
    isPending: isAllowedCategoriesAndProductsPending,
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId: branchData?.id,
    filters: { search: searchProduct },
  });

  const allowedCategoriesOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending ||
      !allowedCategoriesAndProducts ||
      !allowedCategoriesAndProducts['categories']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
          key: 'categories',
          controlledBy: companyGovernmentAllocation?.government_allocation?.controlled_by,
        })(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending])

  const onSuccess = () => {
    setBaseError('')
    queryClient.invalidateQueries({ queryKey: ['clientCompanyBranchAvailableToAllocateSummary'] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', id] })
  }

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocations'] })
    queryClient.invalidateQueries({ queryKey: ['companyGovernmentAllocationEntry', id] })
    queryClient.invalidateQueries({ queryKey: ['clientCompanyBranchAvailableToAllocateSummary'] })
    navigate(`/companies/${companyId}/government-allocation`)
  }

  const onError = (error: ErrorResponse) => {
    handleServerError({ error, setBaseError, setError })
  }

  const { mutate: updateCompanyGovernmentAllocationEntry } = useUpdateCompanyGovernmentAllocationEntry({
    governmentAllocationId: id,
    onSuccess,
    onError,
  })

  const { mutate: uploadCompanyGovernmentAllocationFile, isPending: uploadCompanyGovernmentAllocationFileIsPending } =
    useUpdateCompanyGovernmentAllocationEntryFile({
      governmentAllocationId: id,
      onSuccess,
      onError,
    })

  const { mutate: advanceGovernmentAllocationEntry, isPending: advanceGovernmentAllocationEntryIsPending } =
    useAdvanceGovernmentAllocationEntry({
      id,
      onSuccess: redirectOnSuccess,
      onError,
    })

  const onSubmit: SubmitHandler<GovernmentAllocationEntryUpdateValidationSchema> = (data) => {
    clearErrors()
    setBaseError('')
    updateCompanyGovernmentAllocationEntry(data)
  }

  const submit = handleSubmit(onSubmit)

  const submittableOnBlur = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    if (e.target.value === '') {
      return
    }
    onBlur(e)
    submit()
  }

  useEffect(reset, []);

  return (
    <Box className="flex gap-6 items-start h-[calc(100vh-130px)] shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="w-full flex flex-col gap-6 h-[calc(100vh-178px)] overflow-y-auto">
        {baseError && <p className="text-sm text-danger-soft">{baseError}</p>}

        <p className="text-lg font-bold">
          Detalhes {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
        </p>

        {companyGovernmentAllocation?.last_review?.state === 'disapproved' && (
          <RequestedReview description={companyGovernmentAllocation?.last_review?.description} />
        )}

        <div className="flex gap-6">
          <div>
            <p className="text-sm font-light italic">Valor global do contrato</p>
            <p className="text-lg font-bold">{formatCurrency(Number(company?.granted_credit || 0))}</p>
          </div>

          <div>
            <p className="text-sm font-light italic">Cota de empenho da filial</p>
            <p className="text-lg font-bold">{formatCurrency(Number(branchData?.granted_credit || 0))}</p>
          </div>
        </div>

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? (
          <div className="flex gap-x-2">
            <Controller
              control={control}
              name="office_number"
              defaultValue={companyGovernmentAllocation?.office_number}
              disabled={advanceGovernmentAllocationEntryIsPending}
              render={({ field: { onBlur, ...rest } }) => (
                <TextField
                  label="Número do ofício do Empenho"
                  className="mt-5"
                  {...rest}
                  errorMessage={errors.office_number?.message}
                  onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                />
              )}
            />

            <div className="space-y-2">
              <p className="text-[11px] leading-3">Período de competência</p>

              <div className="flex items-start gap-x-2">
                <Controller
                  control={control}
                  name="government_allocation.start_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.start_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.start_of_document_validity)
                      : undefined
                  }
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  render={({ field: { onBlur, ...rest } }) => (
                    <MaskedTextField
                      label="Início"
                      mask="00/00/0000"
                      LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                      {...rest}
                      errorMessage={errors.government_allocation?.start_of_document_validity?.message}
                      onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="government_allocation.end_of_document_validity"
                  defaultValue={
                    companyGovernmentAllocation?.government_allocation?.end_of_document_validity
                      ? dateTransformer(companyGovernmentAllocation?.government_allocation?.end_of_document_validity)
                      : undefined
                  }
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  render={({ field: { onBlur, ...rest } }) => (
                    <MaskedTextField
                      label="Fim"
                      mask="00/00/0000"
                      LeftIcon={<CalendarDaysIcon strokeWidth={1} />}
                      {...rest}
                      errorMessage={errors.government_allocation?.end_of_document_validity?.message}
                      onBlur={(e) => submittableOnBlur?.(e, onBlur)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-sm font-light italic">N da cota estimativa</p>
              <p className="text-lg font-bold">{companyGovernmentAllocation?.office_number}</p>
            </div>

            <div>
              <p className="text-sm font-light italic">Período de competência</p>
              <p className="text-lg font-bold">
                {companyGovernmentAllocation?.government_allocation?.applicable_start_date &&
                  companyGovernmentAllocation?.government_allocation?.applicable_end_date &&
                  formatDateRange(
                    new Date(companyGovernmentAllocation?.government_allocation?.applicable_start_date),
                    new Date(companyGovernmentAllocation?.government_allocation?.applicable_end_date),
                  )}
              </p>
            </div>
          </>
        )}

        {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' && (
          <FileUploader
            name="file"
            accept="application/pdf, image/png, image/jpg, image/jpeg"
            title="Anexe o Empenho"
            fileName={
              fileName
                ? fileName
                : companyGovernmentAllocation?.file?.url
                  ? getFilenameFromUrl(companyGovernmentAllocation?.file?.url)
                  : undefined
            }
            currentFileUrl={
              file
                ? URL.createObjectURL(file)
                : companyGovernmentAllocation?.file?.url
                  ? companyGovernmentAllocation?.file?.url
                  : undefined
            }
            handleChange={(file) => {
              setFileName(file.name)
              setFile(file)
              uploadCompanyGovernmentAllocationFile(file)
            }}
            disabled={uploadCompanyGovernmentAllocationFileIsPending || advanceGovernmentAllocationEntryIsPending}
          />
        )}

        <Controller
          name="total_value"
          control={control}
          defaultValue={
            companyGovernmentAllocation?.total_value
              ? formatCurrency(Number(companyGovernmentAllocation?.total_value))
              : undefined
          }
          disabled={advanceGovernmentAllocationEntryIsPending}
          render={({ field: { onBlur, ...rest } }) => (
            <CurrencyTextField
              label={`Valor total ${companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do empenho' : 'da cota'
                }`}
              markAsRequired
              maxValue={999_999_999.99}
              className="w-full"
              {...rest}
              onBlur={(e) => submittableOnBlur?.(e, onBlur)}
              errorMessage={errors.total_value?.message}
            />
          )}
        />

        <Accordion
          className="py-1"
          head={
            <p>
              Detalhamento{' '}
              {companyGovernmentAllocation?.government_allocation?.kind === 'pre_paid' ? 'do Empenho' : 'da Cota'}
            </p>
          }
          body={
            <div className="space-y-4">
              <Select
                placeholder={
                  <div
                    className={cn(
                      'flex items-center gap-x-2',
                      !advanceGovernmentAllocationEntryIsPending && 'text-white-800',
                    )}
                  >
                    <SearchIcon />
                    <p>Pesquisar serviços</p>
                  </div>
                }
                isLoading={isAllowedCategoriesAndProductsPending}
                options={
                  allowedCategoriesOptions?.filter((productCategoryOption) => {
                    return !companyGovernmentAllocation.entry_product_categories?.some(
                      ({ product_category }) => product_category?.id === productCategoryOption.value,
                    )
                  }) || []
                }
                onInputChange={setSearchProduct}
                filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
                value=""
                onChange={(newValue) => {
                  setSelectedAllowedCategorySummary({
                    ...allowedCategoriesAndProducts?.categories?.[(newValue as Option).value],
                    id: (newValue as Option).value,
                  })
                }}
                isDisabled={
                  advanceGovernmentAllocationEntryIsPending
                }
              />

              {!!companyGovernmentAllocation.entry_product_categories.length && (
                <EntryGovernmentAllocationDetails
                  companyBranchId={branchData?.id}
                  governmentAllocationId={companyGovernmentAllocation.id}
                  entryProductCategories={companyGovernmentAllocation.entry_product_categories}
                  disabled={advanceGovernmentAllocationEntryIsPending}
                  controlledBy={companyGovernmentAllocation?.government_allocation?.controlled_by}
                />
              )}
            </div>
          }
        />
      </div>

      <Checklist
        requirement_list_summary={companyGovernmentAllocation?.requirement_list_summary}
        className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
        Buttons={() => (
          <Button
            type="button"
            variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
            disabled={!passedEveryRequirement && !advanceGovernmentAllocationEntryIsPending}
            onClick={() => advanceGovernmentAllocationEntry()}
            isLoading={advanceGovernmentAllocationEntryIsPending}
          >
            Enviar para análise
          </Button>
        )}
      />

      <CreateEntryProductCategoryModal
        companyBranchId={branchData?.id}
        allowedCategorySummary={selectedAllowedCategorySummaryRecord}
        onDismiss={() => setSelectedAllowedCategorySummary(undefined)}
        controlledBy={companyGovernmentAllocation?.government_allocation?.controlled_by}
      />
    </Box>
  )
}
